import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import { Box, SxProps, Theme } from '@mui/material';

interface HoverZoomImageProps {
  src: string;
  alt?: string;
  width?: number | string;
  height?: number | string;
  borderColor?: string;
  /** Override default click — defaults to opening src in a new tab */
  onClick?: (e: React.MouseEvent) => void;
  /** Extra styles merged into the small thumbnail */
  thumbSx?: SxProps<Theme>;
  /** objectFit on the thumbnail (default: contain) */
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
}

/**
 * Image that shows a centered, darkened-backdrop preview while hovered.
 * Click behavior is configurable; defaults to opening the image in a new tab.
 */
const HoverZoomImage: React.FC<HoverZoomImageProps> = memo(({
  src,
  alt = '',
  width = 80,
  height = 80,
  borderColor = '#e2e8f0',
  onClick,
  thumbSx,
  objectFit = 'contain',
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = useCallback(() => {
    timeoutRef.current = setTimeout(() => setIsHovered(true), 100);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsHovered(false);
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (onClick) onClick(e);
    else window.open(src, '_blank');
  }, [onClick, src]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <>
      <Box
        component="img"
        src={src}
        alt={alt}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        loading="lazy"
        sx={{
          width,
          height,
          objectFit,
          cursor: 'pointer',
          borderRadius: 1.5,
          ...thumbSx,
        }}
      />
      {isHovered && createPortal(
        <>
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 99998,
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 99999,
              pointerEvents: 'none',
            }}
          >
            <Box
              component="img"
              src={src}
              alt={alt}
              sx={{
                width: '500px',
                height: '500px',
                objectFit: 'contain',
                bgcolor: 'white',
                p: 2,
                borderRadius: 3,
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                border: `3px solid ${borderColor}`,
                maxWidth: '90vw',
                maxHeight: '90vh',
              }}
            />
          </Box>
        </>,
        document.body,
      )}
    </>
  );
});

HoverZoomImage.displayName = 'HoverZoomImage';

export default HoverZoomImage;
